import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRepeat, BoxSeam, Check2, Lightning, PlusLg, Search, Tag, Trash3, XLg } from 'react-bootstrap-icons';
import { useLocale } from '../i18n/LocaleContext';
import {
  fetchBrandDiscounts,
  syncBrandDiscounts,
  createBrandDiscount,
  setBrandDiscountPaused,
  deleteBrandDiscount,
  fetchBrandProducts,
} from '../api/brands';

function fmtDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function fmtAmount(amount, currency) {
  return `${Number(amount).toLocaleString()} ${currency || ''}`.trim();
}

function valueLabel(discount) {
  if (discount.discount_type === 'percentage') return `−${Number(discount.value)}%`;
  return `−${fmtAmount(discount.value, discount.currency)}`;
}

function productImage(p) {
  const imgs = Array.isArray(p.images) ? p.images : [];
  return imgs[0]?.src || imgs[0] || p.image || null;
}

function productListOf(list) {
  return Array.isArray(list) ? list : list?.data ?? list?.products ?? [];
}

const EMPTY_FORM = {
  method: 'automatic',
  code: '',
  title: '',
  discount_type: 'percentage',
  value: '',
  condition: 'none',
  min_subtotal: '',
  min_quantity: '',
  usage_limit: '',
  once_per_customer: false,
  starts_at: '',
  ends_at: '',
  all_products: true,
  sync_shopify: true,
};

function Segment({ value, options, onChange, disabled }) {
  return (
    <div className="dm-segment">
      {options.map(({ key, label }) => (
        <button
          type="button"
          key={key}
          className={`dm-segment__btn${value === key ? ' is-active' : ''}`}
          onClick={() => onChange(key)}
          disabled={disabled}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function DmField({ label, children }) {
  return (
    <div className="dm-field">
      <span className="dm-label">{label}</span>
      {children}
    </div>
  );
}

function ProductThumb({ product }) {
  const img = productImage(product);
  return img
    ? <img className="dm-thumb" src={img} alt="" loading="lazy" />
    : <span className="dm-thumb dm-thumb--empty"><BoxSeam size={13} /></span>;
}

function NewDiscountForm({ brand, currency, onSubmit, onCreated, onClose }) {
  const { t } = useLocale();
  const [form, setForm] = useState(EMPTY_FORM);
  const [products, setProducts] = useState(null);
  const [selected, setSelected] = useState([]);
  const [query, setQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (form.all_products || products !== null) return undefined;
    let alive = true;
    fetchBrandProducts(brand.id)
      .then((list) => { if (alive) setProducts(productListOf(list)); })
      .catch(() => { if (alive) setProducts([]); });
    return () => {
      alive = false;
    };
  }, [brand.id, form.all_products, products]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = products || [];
    return (q ? list.filter((p) => (p.name || '').toLowerCase().includes(q)) : list).slice(0, 60);
  }, [products, query]);

  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [key]: key === 'code' ? value.toUpperCase().replace(/\s/g, '') : value }));
  };
  const choose = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

  const isPicked = (id) => selected.some((p) => p.id === id);
  const toggle = (product) => setSelected((prev) => (
    prev.some((p) => p.id === product.id) ? prev.filter((p) => p.id !== product.id) : [...prev, product]
  ));

  const isCode = form.method === 'code';
  const valueNumber = Number(form.value);
  const conditionValid = form.condition === 'none'
    || (form.condition === 'subtotal' && Number(form.min_subtotal) > 0)
    || (form.condition === 'quantity' && Number.isInteger(Number(form.min_quantity)) && Number(form.min_quantity) >= 1);
  const valid = (!isCode || /^[A-Z0-9_-]{3,32}$/.test(form.code))
    && Number.isFinite(valueNumber) && valueNumber > 0
    && (form.discount_type !== 'percentage' || valueNumber <= 100)
    && conditionValid
    && (form.all_products || selected.length > 0)
    && (!form.ends_at || !form.starts_at || new Date(form.ends_at) > new Date(form.starts_at));

  async function submit() {
    if (!valid || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const created = await onSubmit({
        method: form.method,
        code: isCode ? form.code : null,
        title: form.title.trim() || null,
        discount_type: form.discount_type,
        value: valueNumber,
        min_subtotal: form.condition === 'subtotal' ? Number(form.min_subtotal) : null,
        min_quantity: form.condition === 'quantity' ? Number(form.min_quantity) : null,
        usage_limit: isCode && form.usage_limit !== '' ? Number(form.usage_limit) : null,
        once_per_customer: isCode && form.once_per_customer,
        starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
        product_ids: form.all_products ? [] : selected.map((p) => p.id),
        sync_shopify: form.sync_shopify,
      });
      onCreated(created);
    } catch (err) {
      const key = {
        discount_code_taken: 'discounts.error.taken',
        shopify_reauth_required: 'discounts.error.reauth',
        discount_invalid: 'discounts.error.invalid',
      }[err.message] || 'discounts.error.createFailed';
      setError(t(key));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="dm-panel">
      <button type="button" className="dm-close" onClick={onClose} disabled={submitting} aria-label={t('discounts.form.close')}>
        <XLg size={14} />
      </button>
      <h3 className="dm-title">{t(isCode ? 'discounts.form.titleCoupon' : 'discounts.form.title')}</h3>

      <DmField label={t('discounts.form.kind')}>
        <Segment
          value={form.method}
          onChange={choose('method')}
          disabled={submitting}
          options={[
            { key: 'automatic', label: t('discounts.method.automatic') },
            { key: 'code', label: t('discounts.method.code') },
          ]}
        />
        <p className="dm-hint">{t(`discounts.form.methodHint.${form.method}`)}</p>
      </DmField>

      {isCode ? (
        <DmField label={t('discounts.form.code')}>
          <input className="dm-input dm-input--code" value={form.code} onChange={set('code')} placeholder="SUMMER20" maxLength={32} autoFocus disabled={submitting} />
          <p className="dm-hint">{t('discounts.form.codeHint')}</p>
        </DmField>
      ) : (
        <DmField label={t('discounts.form.name')}>
          <input className="dm-input" value={form.title} onChange={set('title')} placeholder={t('discounts.form.namePlaceholder')} maxLength={120} autoFocus disabled={submitting} />
          <p className="dm-hint">{t('discounts.form.nameHint')}</p>
        </DmField>
      )}

      <DmField label={t('discounts.form.products')}>
        <label className="dm-check">
          <input type="checkbox" checked={form.all_products} onChange={set('all_products')} disabled={submitting} />
          {t('discounts.form.allProducts')}
        </label>
        {!form.all_products && (
          <>
            {selected.length > 0 && (
              <div className="dm-chips">
                {selected.map((p) => (
                  <span className="dm-chip" key={p.id}>
                    <ProductThumb product={p} />
                    <span className="dm-chip__name">{p.name || '—'}</span>
                    <button type="button" onClick={() => toggle(p)} disabled={submitting} aria-label={t('discounts.delete')}>
                      <XLg size={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="dm-search">
              <Search size={13} />
              <input className="dm-input" value={query} placeholder={t('discounts.form.searchProducts')} onChange={(e) => setQuery(e.target.value)} disabled={submitting} />
            </div>
            {products === null ? (
              <p className="dm-hint">{t('shell.loading')}</p>
            ) : products.length === 0 ? (
              <p className="dm-hint">{t('discounts.form.noProducts')}</p>
            ) : (
              <div className="dm-results">
                {shown.map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    className={`dm-result${isPicked(p.id) ? ' is-on' : ''}`}
                    onClick={() => toggle(p)}
                    disabled={submitting}
                  >
                    <ProductThumb product={p} />
                    <span className="dm-result__name">{p.name || '—'}</span>
                    {isPicked(p.id) && <Check2 size={16} className="dm-result__check" />}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </DmField>

      <DmField label={t('discounts.form.type')}>
        <Segment
          value={form.discount_type}
          onChange={choose('discount_type')}
          disabled={submitting}
          options={[
            { key: 'percentage', label: t('discounts.form.typePercentage') },
            { key: 'fixed_amount', label: t('discounts.form.typeFixed') },
          ]}
        />
      </DmField>

      <DmField label={form.discount_type === 'percentage' ? t('discounts.form.valuePercent') : t('discounts.form.valueAmount', { currency: currency || '' })}>
        <input className="dm-input" type="number" min="0" step="0.01" value={form.value} onChange={set('value')} disabled={submitting} />
      </DmField>

      <DmField label={t('discounts.form.condition')}>
        <Segment
          value={form.condition}
          onChange={choose('condition')}
          disabled={submitting}
          options={[
            { key: 'none', label: t('discounts.form.conditionNone') },
            { key: 'subtotal', label: t('discounts.form.conditionSubtotal') },
            { key: 'quantity', label: t('discounts.form.conditionQuantity') },
          ]}
        />
        {form.condition === 'subtotal' && (
          <input className="dm-input dm-input--after" type="number" min="0" step="0.01" placeholder={t('discounts.form.minSubtotal', { currency: currency || '' })} value={form.min_subtotal} onChange={set('min_subtotal')} disabled={submitting} />
        )}
        {form.condition === 'quantity' && (
          <input className="dm-input dm-input--after" type="number" min="1" step="1" placeholder={t('discounts.form.minQuantity')} value={form.min_quantity} onChange={set('min_quantity')} disabled={submitting} />
        )}
      </DmField>

      <div className="dm-grid">
        <DmField label={t('discounts.form.startsAt')}>
          <input className="dm-input" type="datetime-local" value={form.starts_at} onChange={set('starts_at')} disabled={submitting} />
        </DmField>
        <DmField label={t('discounts.form.endsAt')}>
          <input className="dm-input" type="datetime-local" value={form.ends_at} onChange={set('ends_at')} disabled={submitting} />
        </DmField>
        {isCode && (
          <DmField label={t('discounts.form.usageLimit')}>
            <input className="dm-input" type="number" min="1" step="1" placeholder={t('discounts.form.optional')} value={form.usage_limit} onChange={set('usage_limit')} disabled={submitting} />
          </DmField>
        )}
      </div>

      {isCode && (
        <label className="dm-check">
          <input type="checkbox" checked={form.once_per_customer} onChange={set('once_per_customer')} disabled={submitting} />
          {t('discounts.form.oncePerCustomer')}
        </label>
      )}

      <DmField label={t('discounts.form.shopify')}>
        <Segment
          value={form.sync_shopify ? 'sync' : 'local'}
          onChange={(v) => setForm((prev) => ({ ...prev, sync_shopify: v === 'sync' }))}
          disabled={submitting}
          options={[
            { key: 'sync', label: t('discounts.form.shopifySync') },
            { key: 'local', label: t('discounts.form.shopifyLocal') },
          ]}
        />
        <p className="dm-hint">{t(form.sync_shopify ? 'discounts.form.shopifyNote' : 'discounts.form.localNote')}</p>
      </DmField>
      {error && <div className="dm-error">{error}</div>}

      <div className="dm-actions">
        <button type="button" className="dm-btn" onClick={onClose} disabled={submitting}>{t('discounts.form.cancel')}</button>
        <button type="button" className="dm-btn dm-btn--primary" onClick={submit} disabled={!valid || submitting}>
          {submitting ? t('discounts.form.creating') : t('discounts.form.create')}
        </button>
      </div>
    </div>
  );
}

function DiscountList({ discounts, emptyKey, canEdit, busy, onToggle, onRemove }) {
  const { t } = useLocale();
  if (discounts.length === 0) return <div className="panel-card__muted">{t(emptyKey)}</div>;
  return (
    <div className="drop-list">
      {discounts.map((d) => {
        const starts = fmtDate(d.starts_at);
        const ends = fmtDate(d.ends_at);
        const automatic = d.method === 'automatic';
        return (
          <div className="drop-row discount-row" key={d.id}>
            <div className={`drop-row__img discount-row__icon${automatic ? ' is-automatic' : ''}`}>
              {automatic ? <Lightning size={18} /> : <Tag size={18} />}
            </div>
            <div className="drop-row__main">
              <div className="drop-row__name">
                <span className={automatic ? '' : 'discount-row__code'}>{automatic ? d.title : d.code}</span>
                <span className="discount-row__value">{valueLabel(d)}</span>
              </div>
              <div className="drop-row__meta">
                <span>{t(`discounts.method.${automatic ? 'automatic' : 'code'}`)}</span>
                <span>
                  {d.product_ids?.length
                    ? t('discounts.productCount', { n: d.product_ids.length })
                    : t('discounts.allProducts')}
                </span>
                {d.min_subtotal != null && (
                  <span>{t('discounts.minSubtotal', { amount: fmtAmount(d.min_subtotal, d.currency) })}</span>
                )}
                {d.min_quantity != null && <span>{t('discounts.minQuantity', { n: d.min_quantity })}</span>}
                <span>
                  {d.usage_limit != null
                    ? t('discounts.usageOf', { used: d.usage_count, limit: d.usage_limit })
                    : t('discounts.usage', { used: d.usage_count })}
                </span>
                {(starts || ends) && <span>{[starts, ends].filter(Boolean).join(' – ')}</span>}
                {d.once_per_customer && <span>{t('discounts.oncePerCustomer')}</span>}
                {d.source === 'shopify'
                  ? <span className="discount-row__source">{t('discounts.fromShopify')}</span>
                  : <span className={d.shopify_synced ? 'discount-row__source' : 'discount-row__local'}>{t(d.shopify_synced ? 'discounts.syncedShopify' : 'discounts.outfinoOnly')}</span>}
              </div>
            </div>
            <span className={`drop-row__status is-${d.status}`}>{t(`discounts.status.${d.status}`)}</span>
            {canEdit && (d.status === 'active' || d.status === 'paused' || d.status === 'scheduled') && (
              <button className="btn-o btn-o--ghost btn-o--sm" onClick={() => onToggle(d)} disabled={busy === d.id}>
                {d.status === 'paused' ? t('discounts.resume') : t('discounts.pause')}
              </button>
            )}
            {canEdit && (
              <button
                className="btn-o btn-o--ghost btn-o--sm discount-row__delete"
                onClick={() => onRemove(d)}
                disabled={busy === d.id}
                aria-label={t('discounts.delete')}
              >
                <Trash3 size={14} />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function DiscountsTab({ brand, canEdit = false }) {
  const { t } = useLocale();
  const [discounts, setDiscounts] = useState(null);
  const [currency, setCurrency] = useState(null);
  const [creating, setCreating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [busy, setBusy] = useState(null);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let alive = true;
    fetchBrandDiscounts(brand.id, { sync: true })
      .then(({ discounts: list, currency: brandCurrency, syncError }) => {
        if (!alive) return;
        setDiscounts(list);
        setCurrency(brandCurrency);
        if (syncError) setNotice(t(syncError === 'shopify_reauth_required' ? 'discounts.error.reauth' : 'discounts.error.syncFailed'));
      })
      .catch(() => alive && setDiscounts([]));
    return () => {
      alive = false;
    };
  }, [brand.id, t]);

  async function sync() {
    setSyncing(true);
    setNotice('');
    try {
      setDiscounts(await syncBrandDiscounts(brand.id));
      setNotice(t('discounts.synced'));
    } catch (err) {
      setNotice(t(err.message === 'shopify_reauth_required' ? 'discounts.error.reauth' : 'discounts.error.syncFailed'));
    } finally {
      setSyncing(false);
    }
  }

  function handleCreated(created) {
    setCreating(false);
    setNotice(t(created.shopify_synced ? 'discounts.created' : 'discounts.createdLocal', { code: created.code || created.title }));
    setDiscounts((prev) => [created, ...(prev || [])]);
  }

  async function togglePaused(discount) {
    setBusy(discount.id);
    try {
      const updated = await setBrandDiscountPaused(brand.id, discount.id, discount.status !== 'paused');
      setDiscounts((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
    } catch {
      setNotice(t('discounts.error.updateFailed'));
    } finally {
      setBusy(null);
    }
  }

  async function remove(discount) {
    if (!window.confirm(t(discount.shopify_synced ? 'discounts.confirmDelete' : 'discounts.confirmDeleteLocal', { code: discount.code || discount.title }))) return;
    setBusy(discount.id);
    try {
      await deleteBrandDiscount(brand.id, discount.id);
      setDiscounts((prev) => prev.filter((d) => d.id !== discount.id));
    } catch {
      setNotice(t('discounts.error.deleteFailed'));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="panel-card">
      <div className="panel-card__row">
        <h2 className="panel-card__title">{t('discounts.title')}</h2>
        <div className="discount-toolbar">
          <button className="btn-o btn-o--ghost" onClick={sync} disabled={syncing}>
            <ArrowRepeat size={14} style={{ marginRight: 6 }} className={syncing ? 'is-spinning' : ''} />
            {syncing ? t('discounts.syncing') : t('discounts.sync')}
          </button>
          {canEdit && !creating && (
            <button className="btn-o btn-o--brand" onClick={() => { setNotice(''); setCreating(true); }}>
              <PlusLg size={14} style={{ marginRight: 6 }} />
              {t('discounts.new')}
            </button>
          )}
        </div>
      </div>
      <p className="panel-card__muted">{t('discounts.subtitle')}</p>
      {currency && <p className="discount-currency">{t('discounts.currency', { currency })}</p>}

      {notice && <div className="dash__notice">{notice}</div>}

      {creating && (
        <NewDiscountForm
          brand={brand}
          currency={currency}
          onSubmit={(input) => createBrandDiscount(brand.id, input)}
          onCreated={handleCreated}
          onClose={() => setCreating(false)}
        />
      )}

      {discounts === null ? (
        <div className="panel-card__muted">{t('shell.loading')}</div>
      ) : (
        ['automatic', 'code'].map((method) => (
          <section className="discount-section" key={method}>
            <h3 className="discount-section__title">{t(`discounts.section.${method}`)}</h3>
            <DiscountList
              discounts={discounts.filter((d) => (d.method || 'code') === method)}
              emptyKey={`discounts.empty.${method}`}
              canEdit={canEdit}
              busy={busy}
              onToggle={togglePaused}
              onRemove={remove}
            />
          </section>
        ))
      )}
    </div>
  );
}
